import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";

export type TLanguage = "de" | "en";

type TLanguageContext = {
  language: TLanguage;
  switchLanguage: (newLanguage: TLanguage) => void;
};

export const DefaultLanguage: TLanguage = "en";
const LanguageContext = createContext<TLanguageContext | null>(null);

const LocalStorageLanguageKey = "language";

const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<TLanguage>(currentLanguage());
  const { i18n } = useTranslation();

  const switchLanguage = useCallback(
    (newLanguage: TLanguage) => {
      setLanguage(newLanguage);
      i18n.changeLanguage(newLanguage);
      moment.locale(i18n.language);
      localStorage.setItem(LocalStorageLanguageKey, newLanguage);
    },
    [i18n]
  );

  return (
    <LanguageContext.Provider value={{ language, switchLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;

export const useLanguage = () => {
  return useContext(LanguageContext) as TLanguageContext;
};

export const currentLanguage = () => {
  const language =
    (localStorage.getItem(LocalStorageLanguageKey) as TLanguage) ||
    DefaultLanguage;
  return language;
};
